import { useEffect, useState, useCallback, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getImageSrc } from '~/helpers/image';
import { cn } from '~/helpers/tw';

export default function CarouselSlideShow() {
    const { t } = useTranslation();
    const [mobileTitleTop, setMobileTitleTop] = useState<string | undefined>(
        undefined
    );
    const [mobileNavTop, setMobileNavTop] = useState<string | undefined>(
        undefined
    );
    const updateMobilePosition = useCallback(() => {
        const form = document.getElementById('formLoginContainer');
        if (!form) {
            setMobileTitleTop(undefined);
            setMobileNavTop(undefined);
            return;
        }
        const rect = form.getBoundingClientRect();
        const offset = 8; // px abaixo do form

        // medir a altura do título (maior entre os h1 no portal)
        let titleHeight = 0;
        document.querySelectorAll('.carouselTitleMobile').forEach((el) => {
            const r = (el as HTMLElement).getBoundingClientRect();
            if (r.height > titleHeight) titleHeight = r.height;
        });
        if (titleHeight === 0) titleHeight = 40;

        const titleTopPx = Math.round(rect.bottom + offset); // rect.bottom já é relativo ao viewport (ideal para position:fixed)
        const navTopPx = Math.round(titleTopPx + titleHeight + 8); // 8px gap entre título e nav

        setMobileTitleTop(`${titleTopPx}px`);
        setMobileNavTop(`${navTopPx}px`);
    }, []);

    // debounce via rAF — evita jitter em scroll/resize
    const updateMobilePositionRaf = useCallback(() => {
        const id = (window as any).carousel_title_raf;
        if (id) cancelAnimationFrame(id);
        (window as any).carousel_title_raf = requestAnimationFrame(() => {
            updateMobilePosition();
        });
    }, [updateMobilePosition]);

    const carouselImages = [
        '/Common/Images/Carousel/Login/image-1.jpeg',
        '/Common/Images/Carousel/Login/image-2.jpeg',
        '/Common/Images/Carousel/Login/image-3.jpeg',
        '/Common/Images/Carousel/Login/image-4.jpeg',
    ];

    const carouselTotalImages = carouselImages.length;

    const [currentSlide, setCurrentSlide] = useState(0);

    const setSlide = useCallback(() => {
        setCurrentSlide((prevSlide) => (prevSlide + 1) % carouselTotalImages);
    }, [carouselTotalImages]);

    useEffect(() => {
        const interval = setInterval(setSlide, 5000);
        return () => clearInterval(interval);
    }, [carouselTotalImages, setSlide]);

    // recalcula posição no load, resize e scroll; também observa mudanças no form
    useEffect(() => {
        // initial + ensure after load
        updateMobilePosition();

        // ensure after load
        window.addEventListener('load', updateMobilePosition);

        // position on resize
        window.addEventListener('resize', updateMobilePositionRaf);

        // position on scroll
        window.addEventListener('scroll', updateMobilePositionRaf, {
            passive: true,
        });

        // observe DOM changes inside the form (height/content changes)
        const observer = new MutationObserver(updateMobilePosition);
        const form = document.getElementById('formLoginContainer');
        if (form) {
            observer.observe(form, {
                childList: true,
                subtree: true,
                attributes: true,
            });
        }
        return () => {
            window.removeEventListener('load', updateMobilePosition);
            window.removeEventListener('resize', updateMobilePositionRaf);
            window.removeEventListener('scroll', updateMobilePositionRaf);
            observer.disconnect();
        };
    }, [updateMobilePosition]);

    return (
        <div className="relative flex h-full w-full flex-col [@media(min-width:1024px)]:flex-row [@media(min-width:1024px)]:items-stretch">
            <div
                className="carousel relative h-full w-full"
                id="carouselContainer"
            >
                {/* gradient overlay: visible on small screens, hidden at md+ */}
                <div
                    className="pointer-events-none absolute inset-0 z-10 [@media(max-width:1023px)]:bg-[linear-gradient(to_bottom,rgba(255,255,255,0)_0%,rgba(255,255,255,1)_100%)]"
                    aria-hidden="true"
                    id="carousel-gradient"
                />

                {/* === carousel navigation buttons: (visible on md > 768px) === */}
                <div
                    id="carouselNavigationContainerDesktop"
                    className="absolute left-0 right-0 top-1/3 z-[11] mx-[15%] mb-4 hidden list-none justify-center p-0 md:flex"
                >
                    {carouselImages.map((_, i) => (
                        <button
                            key={`desktop-${i}`}
                            type="button"
                            aria-label={t('auth.carousel.goToSlide', {
                                number: i + 1,
                            })}
                            onClick={() => setCurrentSlide(i)}
                            className={`${currentSlide === i ? 'w-[24px] opacity-100' : 'w-[8px] opacity-70'} transition-width mx-[3px] h-[5px] flex-initial cursor-pointer rounded-full bg-white duration-500`}
                        />
                    ))}
                </div>

                {/* ===  carousel navigation buttons: (visible on md <= 768px) === 
                    Posicionamento dinamicamente abaixo de #formLoginContainer.
                    Usamos position:fixed + top calculado, atualizando em resize/scroll.
                */}

                {typeof document !== 'undefined' &&
                    createPortal(
                        <>
                            {/* H1 (visible on md <= 768px) */}
                            <div
                                id="carouselTitlesMobileContainer"
                                className="pointer-events-none fixed left-1/2 z-20 [@media(min-width:1024px)]:hidden"
                                style={
                                    mobileTitleTop
                                        ? ({
                                              top: mobileTitleTop,
                                              transform: 'translateX(-50%)',
                                          } as CSSProperties)
                                        : ({
                                              bottom: '140px',
                                              transform: 'translateX(-50%)',
                                          } as CSSProperties)
                                }
                            >
                                {carouselImages.map((_, i) => (
                                    <h1
                                        key={`mobile-title-${i}`}
                                        id={`carouselTitleMobile_${i + 1}`}
                                        className="carouselTitleMobile w-[27.875rem] absolute left-1/2 top-0 -translate-x-1/2 text-center text-sm lg:text-lg font-bold tracking-normal text-blue-700 transition-opacity duration-1000 ease-in-out sm:w-[27.875rem]"
                                        style={
                                            {
                                                opacity:
                                                    currentSlide === i ? 1 : 0,
                                                pointerEvents: 'none',
                                            } as CSSProperties
                                        }
                                        dangerouslySetInnerHTML={{
                                            __html: t(
                                                `auth.carousel.quote${i + 1}`
                                            ),
                                        }}
                                    />
                                ))}
                            </div>
                            <div
                                id="carouselNavigationContainerMobile"
                                className={cn(
                                    'pointer-events-auto fixed left-1/2 z-[20] mb-4 flex -translate-x-1/2 transform list-none justify-center p-0 [@media(min-width:1024px)]:hidden'
                                )}
                                style={
                                    mobileNavTop
                                        ? ({
                                              top: mobileNavTop,
                                          } as CSSProperties)
                                        : ({ bottom: '100px' } as CSSProperties)
                                }
                            >
                                {carouselImages.map((_, i) => (
                                    <button
                                        key={`mobile-${i}`}
                                        type="button"
                                        aria-label={t(
                                            'auth.carousel.goToSlide',
                                            { number: i + 1 }
                                        )}
                                        onClick={() => setCurrentSlide(i)}
                                        className="mx-[3px] flex h-11 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                    >
                                        <span
                                            className={`${currentSlide === i ? 'w-[24px] opacity-100' : 'w-[8px] opacity-70'} block h-[5px] rounded-full bg-blue-700 transition-all duration-300`}
                                        />
                                    </button>
                                ))}
                            </div>
                        </>,
                        document.body
                    )}

                {/* carousel slides images */}
                {carouselImages.map((src, i) => (
                    <div
                        id={`image-${i + 1}`}
                        key={i}
                        className={`transition-opacity duration-1000 ${currentSlide === i ? 'opacity-100' : 'opacity-0'}`}
                    >
                        <img
                            alt={`carousel_${i + 1}`}
                            className="max-[768px]:object-top absolute inset-0 h-full w-full object-cover"
                            src={getImageSrc(src)}
                        />

                        {/* H1 (visible on md > 768px) */}
                        <h1
                            id={`carouselTitleDesktop_${i + 1}`}
                            className="carouselTitleDesktop [@media(min-width:1281px)]:text-3xl absolute left-1/2 top-1/4 hidden -translate-x-1/2 -translate-y-1/2 text-center text-xl font-bold text-white md:hidden [@media(min-width:1024px)]:block"
                            dangerouslySetInnerHTML={{
                                __html: t(`auth.carousel.quote${i + 1}`),
                            }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
